/**
 * The theme control (Matt, 2026-10-18; brand C builds, production since the same day): Automatic (the default: the device's
 * setting, followed live by the CSS media query), Light or Dark. The choice is kept on this device only (localStorage,
 * never sent; the app works without it) and applied before the first paint by the inline head script
 * (src/build/themeHead.ts), which reads the same key. A button in the header names the current setting; it opens a
 * menu of the three, each marked when current; choosing returns focus to the button, whose name then says the new one.
 */
type El = <K extends keyof HTMLElementTagNameMap>(tag: K, attrs?: Record<string, string>, ...children: (Node | string)[]) => HTMLElementTagNameMap[K];

export type Theme = 'auto' | 'light' | 'dark';
export const THEMES: readonly Theme[] = ['auto', 'light', 'dark'];
export const THEME_KEY = 'magia-li.theme';

export function loadTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
}

/** On the page at once (`data-theme` on <html>; none = automatic), then kept on the device when it can be. */
export function setTheme(theme: Theme): void {
  if (theme === 'auto') delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
  try {
    if (theme === 'auto') localStorage.removeItem(THEME_KEY);
    else localStorage.setItem(THEME_KEY, theme);
  } catch {
    // Private mode or blocked storage: the choice lasts until the page is closed.
  }
}

const ICONS: Record<Theme, string> = {
  // Half a circle filled: as the device is set.
  auto: '<circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16Z" fill="currentColor"/>',
  light: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>',
  dark: '<path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z"/>',
};
function icon(el: El, theme: Theme): HTMLElement {
  const span = el('span', { class: 'theme-icon', 'aria-hidden': 'true' });
  span.innerHTML = `<svg viewBox="0 0 24 24" focusable="false" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">${ICONS[theme]}</svg>`;
  return span;
}

/** The header control. `tr` gives the page's language; the menu closes on Esc, on choosing and when focus leaves it. */
export function themeMenu(el: El, tr: (key: string) => string): HTMLElement {
  const current = loadTheme();
  const name = (t: Theme) => tr(`theme.${t}`);
  const toggle = el('button', {
    type: 'button', id: 'theme-switch', class: 'theme-switch secondary', 'aria-expanded': 'false', 'aria-controls': 'theme-menu',
    // The visible name (the setting, hidden on a phone) is part of the accessible name (WCAG 2.5.3).
    'aria-label': `${tr('theme.label')}: ${name(current)}`,
  }, icon(el, current), el('span', { class: 'theme-name' }, name(current)));
  const menu = el('ul', { id: 'theme-menu', class: 'lang-menu theme-menu', hidden: '' },
    ...THEMES.map((t) => {
      const b = el('button', { type: 'button', 'data-theme-choice': t, ...(t === current ? { 'aria-current': 'true' } : {}) }, icon(el, t), el('span', {}, name(t)));
      b.addEventListener('click', () => {
        setTheme(t);
        wrap.replaceWith(themeMenu(el, tr));
        document.getElementById('theme-switch')?.focus();
      });
      return el('li', {}, b);
    }));
  const open = (yes: boolean) => {
    menu.hidden = !yes;
    toggle.setAttribute('aria-expanded', String(yes));
  };
  toggle.addEventListener('click', () => {
    open(!!menu.hidden);
    (menu.hidden ? toggle : menu.querySelector<HTMLElement>('[aria-current="true"]'))?.focus();
  });
  const wrap = el('div', { class: 'lang theme' }, toggle, menu);
  wrap.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.hidden) {
      open(false);
      toggle.focus();
    }
  });
  wrap.addEventListener('focusout', (e) => {
    if (!menu.hidden && e.relatedTarget !== null && !wrap.contains(e.relatedTarget as Node | null)) open(false);
  });
  return wrap;
}
